import React from 'react';
import axios from "axios";

import { useMantineTheme, Box, Group, Flex, ActionIcon, Card, SimpleGrid, Text, Avatar } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowsSort, IconUser } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";

import { BE_API_THREE, utils, role } from "../../utils/globalVariables";
import { regexNoStriped, getPropInSubLevelRole } from "../../utils/globalFunctions";

import AmountOfData from "../general/AmountOfData";
import ModalWrapperWithoutFooter from "../general/ModalWrapperWithoutFooter";
import LoaderLine from "../general/LoaderLine";
import SortMenu from "../general/SortMenu";
import NoData from "../general/NoData";
import SearchInputComponent from "../../components/general/SearchInputComponent";

const DeliveryManSelector = ({
    stateShow,
    fnShow,
    fnOnSelect,
    stateData
}) => {

    const mountedRef = React.useRef(true);

    const theme = useMantineTheme();
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorGetData: 'deliveryManSelector-1',
    })

    const sortList = {
        userName: {
            slug: 'user_name',
            caption: 'Nama',
            type: utils.sortType.string.slug
        },
        userGender: {
            slug: 'user_gender',
            caption: 'Gender',
            type: utils.sortType.integer.slug
        },
        userIsActive: {
            slug: 'user_is_active',
            caption: 'Status',
            type: utils.sortType.integer.slug
        },
        userLevel: {
            slug: 'user_level',
            caption: 'Jabatan',
            type: utils.sortType.string.slug
        },
    }

    const [data, setData] = React.useState({
        data: [],
        dataCount: 0,
        dataFlag: 'idle',
        dataLoading: false,
        dataRefresh: new Date(),
        dataOffset: 0,
        dataSortSelected: sortList.userLevel,
        dataSearchInputForBE: '',

        filterData: {
            drawer: false,
        },
    });

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const fnSort = (stateSortSelected) => {
        setData(d => ({
            ...d,
            dataLoading: true,
            dataSortSelected: {
                ...d.dataSortSelected,
                slug: d.dataSortSelected.slug[0] === '-' ? stateSortSelected.slug : '-' + stateSortSelected.slug,
                caption: stateSortSelected.caption,
                type: stateSortSelected.type
            }
        }));
    };

    const AmountOfDataComponent = () => {
        return (
            <AmountOfData
                stateDataLimit={utils.dataLimit}
                stateDataOffset={data.dataOffset}
                stateAmountDataAll={data.dataCount}
                stateDataFlag={data.dataFlag}
                stateSuffix=''

                fnFirstData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnLastData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnPreviousData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnNextData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}
            />
        )
    }

    React.useEffect(() => {
        setData(d => ({ ...d, dataLoading: true }));

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);
        formDT.append('data_limit', utils.dataLimit);
        formDT.append('data_offset', data.dataOffset);
        formDT.append('order_by', regexNoStriped(data.dataSortSelected.slug));
        formDT.append('order_type', data.dataSortSelected.slug[0] === '-' ? 'asc' : 'desc');

        formDT.append('flag_all', utils.flagAll);
        formDT.append('filter_search', data.dataSearchInputForBE);
        formDT.append('filter_is_active', stateData.isActive);
        formDT.append('filter_gender', stateData.gender);
        formDT.append('user_flag', JSON.stringify([role.delivery.slugLevel, role.owner.slugLevel]));

        axios({
            method: 'post',
            url: BE_API_THREE + '/get_delivery_man',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, dataLoading: false }));

            if (response.data.flag_status === 'isset') {
                setData(d => ({
                    ...d,
                    data: response.data.data,
                    dataCount: response.data.data_count,
                    dataFlag: response.data.data.length > 0 ? true : false,
                }));
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({
                ...d,
                dataLoading: false,
            }));

            notifications.show({
                id: notificationId.current.errorGetData,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }, [handleSetLoginContext, loginContext.syswebapp, data.dataRefresh, data.dataSearchInputForBE, data.dataSortSelected.slug, data.dataOffset, stateData.isActive, stateData.gender])

    return (
        <ModalWrapperWithoutFooter
            stateShow={stateShow}
            fnHide={fnShow}
            stateHideOnClickOutSide={true}
            stateTitle={String('Daftar Delivery Man').toUpperCase()}
            stateSize='md'
            stateOverlayZIndex={201}
            stateWithCloseButton={true}
        >
            <Box
                sx={{
                    position: 'relative'
                }}
            >
                <Group
                    noWrap
                    mb='sm'
                    pb="sm"

                    sx={{
                        borderBottom: `1px solid ${theme.colorScheme === 'light' ? theme.colors.gray[3] : theme.colors.dark[5]}`
                    }}
                >
                    <SortMenu
                        stateSortList={sortList}
                        stateSortSelected={data.dataSortSelected}
                        stateComponentTarget={<ActionIcon
                            size='lg'
                            variant="default"
                            radius='xl'
                        >
                            <IconArrowsSort size='1.25rem' stroke={1} />
                        </ActionIcon>}
                        fnSort={fnSort}
                    />

                    <SearchInputComponent
                        statePlaceHolder='Search Delivery Man'
                        fnDoSearch={(val) => setData(d => ({
                            ...d,
                            dataOffset: 0,
                            dataSearchInputForBE: val,
                            dataRefresh: new Date(),
                        }))}
                    />
                </Group>

                {data.dataLoading &&
                    <LoaderLine
                        statePosition='absolute'
                        stateIsTop={false}
                    />
                }
            </Box>

            {data.data.length > 0 &&
                <SimpleGrid
                    cols={1}
                    spacing='xs'
                >
                    {Object.keys(data.data).map((key, index) => {
                        return (
                            <Card
                                key={key}
                                shadow=''
                                radius="md"
                                withBorder={true}

                                sx={(tema) => ({
                                    borderColor: tema.colorScheme === 'light' ? tema.colors.gray[2] : tema.colors.dark[7],
                                    backgroundColor: tema.colorScheme === 'light' ? tema.colors.gray[0] : tema.colors.dark[6],
                                    overflow: 'unset',
                                    cursor: 'pointer',
                                    transition: '200ms',
                                    '&:hover': {
                                        borderColor: tema.colorScheme === 'light' ? tema.colors.blue[2] : tema.fn.rgba(tema.colors.blue[6], 0.5),
                                        backgroundColor: tema.colorScheme === 'light' ? tema.colors.blue[0] : tema.fn.rgba(tema.colors.blue[6], 0.1),
                                    }
                                })}

                                onClick={() => fnOnSelect({
                                    id: data.data[key].user_id,
                                    name: data.data[key].user_name,
                                    gender: data.data[key].user_gender,
                                    level: data.data[key].user_level,
                                    subLevel: data.data[key].user_sub_level,
                                    isActive: data.data[key].user_is_active,
                                })}
                            >
                                <Card.Section p='sm'>
                                    <Group align='flex-start'>
                                        <Avatar
                                            radius="xl"

                                            styles={(tema) => ({
                                                placeholder: {
                                                    backgroundColor: tema.colorScheme === 'light' ? tema.colors.gray[1] : tema.colors.dark[4]
                                                }
                                            })}
                                        >
                                            <IconUser
                                                size='1.5rem'
                                            />
                                        </Avatar>

                                        <Box>
                                            <Text size="sm" fw={500}>
                                                {data.data[key].user_name}
                                            </Text>

                                            <Text size="xs">
                                                {getPropInSubLevelRole(data.data[key].user_sub_level, 'caption', '-')}
                                            </Text>
                                        </Box>
                                    </Group>
                                </Card.Section>
                            </Card>
                        )
                    })}
                </SimpleGrid>
            }

            {data.data.length <= 0 &&
                <NoData />
            }

            <Flex
                justify="flex-end"
                align="center"
                direction="row"
                wrap='nowrap'
                mt='lg'
            >
                <AmountOfDataComponent />
            </Flex>
        </ModalWrapperWithoutFooter>
    )
}

export default DeliveryManSelector;